import { CalendarOutlined } from "@ant-design/icons";
import { DatePicker, Dropdown, Menu, Space } from "antd";
import { format } from "date-fns";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import {
  BankFeedAccountLinkingData,
  INITIAL_ACCOUNT_LENGTH,
} from "./constants";
import { GenericBankFeedsButton } from "./GenericBankFeedsButton";
import ScrollableComponent from "./ScrollableComponent";
import {
  getFormattedBankFeedAccounts,
  trackAmplitudeEvent,
} from "./utils/shared";

const DATE_FORMAT = "MM/dd/yyyy";
const DATE_PICKER_FORMAT = "MM/DD/yyyy";

enum DateTypes {
  TODAY = "today",
  THIS_MONTH = "thisMonth",
  THIS_YEAR = "thisYear",
  LAST_YEAR = "lastYear",
  CUSTOM_DATE = "customDate",
}

function convertCamelToWords(input: string): string {
  return input
    .replace(/([a-z])([A-Z])/g, "$1 $2") // Insert space before capital letters.
    .replace(/^./, (str) => str.toUpperCase()); // Capitalize first letter.
}

export const GenericBankFeedsLinkStepTwoComponent = ({
  setStep,
  setRetryCallback,
  setTransactionStartDate,
  setRetryableErrorMessage,
  confirmedBankFeedAccountLinkingData,
}: {
  setStep: (value: number) => void;
  setRetryCallback: (fn: () => void) => void;
  setTransactionStartDate: (value: string) => void;
  setRetryableErrorMessage: (value: string) => void;
  confirmedBankFeedAccountLinkingData: BankFeedAccountLinkingData[];
}) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [customDate, setCustomDate] = useState(format(new Date(), DATE_FORMAT));

  const updateRetryableErrorMessage = (message: string) => {
    trackAmplitudeEvent("Retryable error occurred", { errorMessage: message });
    setRetryableErrorMessage(message);
  };

  const selectStartDate = (e: any) => {
    const keyParts = e.key.split(":");

    if (keyParts[0] === "customDate") {
      setCalendarOpen(!calendarOpen);
      return;
    }

    if (calendarOpen) {
      setCalendarOpen(!calendarOpen);
    }

    const date = keyParts[1];
    trackAmplitudeEvent("Start date selected", { selectedDate: date });
    setSelectedDate(date);
    setDropdownOpen(false);
  };

  const selectCustomDate = (value: any, dateString: any) => {
    // Use luxon to format the date without adjusting for timezone.
    const formattedDate = DateTime.fromFormat(dateString, DATE_FORMAT).toISO({
      includeOffset: false,
    });

    if (!formattedDate) {
      trackAmplitudeEvent("Error selecting custom date", { dateString });
      setRetryableErrorMessage("Please try selecting a different date.");
      return;
    }

    trackAmplitudeEvent("Custom date selected", { customDate: dateString });
    setCustomDate(formattedDate);
    setSelectedDate(dateString);
    setCalendarOpen(false);
    setDropdownOpen(false);
  };

  const overrideDate = (
    originalDate: Date,
    day?: number,
    month?: number,
    year?: number,
  ) => {
    const newDate = originalDate;
    newDate.setDate(day ?? newDate.getDay());
    newDate.setMonth(month ?? newDate.getMonth());
    newDate.setFullYear(year ?? newDate.getFullYear());
    return newDate;
  };

  const getCustomDatePicker = () => {
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <DatePicker
          style={{ width: "0px", height: "0px", visibility: "hidden" }}
          open={calendarOpen}
          onChange={selectCustomDate}
          allowClear={false}
          format={DATE_PICKER_FORMAT}
          disabledDate={(current) => {
            // Disable dates after today.
            return current && current.toDate() > new Date();
          }}></DatePicker>
        <CalendarOutlined
          onClick={(e) => {
            // If we click on the calendar, don't count this as selecting a dropdown element.
            e.stopPropagation();
            setCalendarOpen(!calendarOpen);
          }}></CalendarOutlined>
      </div>
    );
  };

  const createMenuItem = (dateType: DateTypes, date?: Date) => {
    const formattedDate = date ? format(date, DATE_FORMAT) : undefined;

    // We append the date type to the date because the date itself is not guaranteed unique,
    // e.g. if today is the first of this month and/or year.
    const key = `${dateType.toString()}:${formattedDate}`;
    return (
      <Menu.Item key={key}>
        <div
          className="flex flex-column"
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}>
          <div style={{ width: "170px" }}>
            <b>{convertCamelToWords(dateType)}</b>{" "}
            {formattedDate ? formattedDate : ""}
          </div>
          <div
            onClick={(e) => {
              // Stop dropdown selection from being triggered when calendar is open.
              e.stopPropagation();
            }}
            style={{
              display: "flex",
              alignItems: "center",
            }}>
            {dateType === DateTypes.CUSTOM_DATE && getCustomDatePicker()}
          </div>
        </div>
      </Menu.Item>
    );
  };

  const getSelectableStartingDates = () => {
    const now = new Date();

    return (
      <Menu onClick={selectStartDate}>
        {createMenuItem(DateTypes.TODAY, now)}
        {createMenuItem(DateTypes.THIS_MONTH, overrideDate(now, 1))}
        {createMenuItem(DateTypes.THIS_YEAR, overrideDate(now, 1, 0))}
        {createMenuItem(
          DateTypes.LAST_YEAR,
          overrideDate(now, 1, 0, now.getFullYear() - 1),
        )}
        {createMenuItem(DateTypes.CUSTOM_DATE)}
      </Menu>
    );
  };

  const confirmHandler = () => {
    trackAmplitudeEvent("Confirming account links and transaction start date", {
      selectedDate,
      accountLinkingData: confirmedBankFeedAccountLinkingData,
    });

    if (!selectedDate) {
      updateRetryableErrorMessage(
        "Please make sure a date is selected before proceeding.",
      );
      return;
    }

    // Use luxon to format the date without adjusting for timezone.
    const formattedDate = DateTime.fromFormat(selectedDate, DATE_FORMAT).toISO({
      includeOffset: false,
    });

    if (!formattedDate) {
      updateRetryableErrorMessage(
        "Please make sure a date is selected before proceeding.",
      );
      return;
    }

    setTransactionStartDate(formattedDate);
    setStep(3);
  };

  useEffect(() => {
    setRetryCallback(() => {
      return () => {
        setSelectedDate(null);
      };
    });
  }, []);

  return (
    <>
      <div className="pt-4 pr-12 pl-12">
        <div className="text-sm" style={{ textAlign: "center", fontSize: 10 }}>
          STEP TWO
        </div>
        <div className="text-lg font-bold pb-2" style={{ textAlign: "center" }}>
          Choose a start date for your transaction history
        </div>
        <div className="text-sm" style={{ textAlign: "center" }}>
          We’ll begin pulling transactions from this date onward. Select a date
          you want bank feeds to start syncing with your records.
        </div>
      </div>

      <div style={{ minHeight: 340 }}>
        <div
          className="m-3"
          style={{
            border: "2px solid lightgray",
            borderRadius: "8px",
            position: "relative",
            minHeight: 170,
          }}>
          <ScrollableComponent
            minHeight={52}
            maxHeight={210}
            classStr="m-1"
            scrollable={
              confirmedBankFeedAccountLinkingData.length >
              INITIAL_ACCOUNT_LENGTH
            }>
            {getFormattedBankFeedAccounts(confirmedBankFeedAccountLinkingData)}
          </ScrollableComponent>
          <div className="pt-4 pb-4 pl-2 pr-2">
            <div
              className="font-bold"
              style={{ textAlign: "left", fontSize: 13 }}>
              We'll pull transactions from your selected accounts from the date
              chosen.
            </div>
            <div style={{ textAlign: "left", fontSize: 12 }}>
              The available window for historical transactions may be limited by
              your financial institution.
            </div>
            <div className="pt-2" style={{ marginLeft: "auto" }}>
              <Dropdown
                visible={dropdownOpen}
                overlay={getSelectableStartingDates()}
                trigger={["click"]}>
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    setDropdownOpen(!dropdownOpen);
                  }}
                  style={{
                    outline: "1px solid #d3d3d3",
                    outlineOffset: 2,
                    borderRadius: 4,
                    padding: "2px 8px",
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}>
                  <Space style={{ fontSize: 12 }}>
                    {selectedDate ?? "Select start date"}
                    <FiChevronDown />
                  </Space>
                </a>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>

      <GenericBankFeedsButton
        text="Confirm"
        onClick={confirmHandler}
        disabled={!selectedDate}
      />
    </>
  );
};
